// Single source of truth for landing-page FAQ content.
// Consumed by FAQ.tsx (full accordion + JSON-LD) and ShortFAQ.tsx (3-card summary).

export type FaqItem = { q: string; a: string };

export const shortFaqs: FaqItem[] = [
  {
    q: "What is Myelina Health for?",
    a: "Myelina Health is intended as a wellness and informational tool for adult women living with MS. It helps organize self-reported energy and context so personal patterns are easier to review. It does not diagnose, treat, predict a relapse, recommend medication changes, or replace a qualified care team.",
  },
  {
    q: "Does Myelina predict my energy?",
    a: "No. The current approved experience is retrospective. It shows information you chose to log and may surface possible tendencies with uncertainty. Those observations can be incomplete, do not prove cause, and do not say what will happen tomorrow.",
  },
  {
    q: "How is my information handled?",
    a: "The current Privacy Policy describes the web service, the service providers used to operate it, and available export, deletion, and sharing controls. Optional features should remain off until you choose them and receive the current written explanation for that product version.",
  },
];

export const longFaqs: FaqItem[] = [
  {
    q: "Is Myelina Health a medical device?",
    a: "Myelina is designed and marketed for wellness and informational use. It is not intended to diagnose, treat, cure, or prevent disease; predict an MS relapse or progression; recommend medication or treatment changes; provide clinician monitoring; or replace a care team. Regulatory status depends on the actual functions and intended use, so Myelina does not rely on a categorical label as a substitute for qualified review.",
  },
  {
    q: "Does Myelina predict higher- or lower-energy hours?",
    a: "No. The current approved experience is retrospective. Myelina can organize the entries you choose to make and may identify possible tendencies in your own history. A tendency can be incomplete or wrong, does not prove causation, and should not be used for medical decisions.",
  },
  {
    q: "Can Myelina predict a bad day, relapse, or disease activity?",
    a: "No. Myelina does not currently claim to predict a bad day, relapse, progression, or disease activity. It does not interpret new or worsening symptoms. Contact a qualified healthcare professional for medical questions and local emergency services for an emergency.",
  },
  {
    q: "Is fatigue my fault when a day does not go as planned?",
    a: "No. Fatigue and missed check-ins are not personal failures. Myelina is intended to help you review what you recorded without blame. It should not pressure you to collect more data, follow a recommendation, or continue an activity despite symptoms.",
  },
  {
    q: "Do I need a wearable?",
    a: "No. A no-wearable pathway is required. Any wearable connection is optional, product-version specific, and subject to separate permission and data-use disclosures. Device information is context, not a more truthful replacement for your own experience, and correlation does not prove cause.",
  },
  {
    q: "Who may request access?",
    a: "Myelina is being developed for adult women living with MS who want non-clinical support for reviewing energy and daily context. Access is currently limited to small invitation waves, and eligibility depends on the current written pilot terms and jurisdictional review.",
  },
  {
    q: "How do I request an invitation?",
    a: "Use the request-access form and provide only the information it asks for. Submitting a request does not guarantee access or require you to enroll. If invited, you should receive the current eligibility, product-limit, privacy, consent, support, and exit information before onboarding.",
  },
  {
    q: "Is my information private?",
    a: "Privacy depends on the exact product version, feature, service provider, and data flow. The current Privacy Policy describes the web service and its processors. Review that written information before using optional AI, email, payment, wearable, sharing, or research features. Do not rely on a verbal promise that nothing is ever processed or shared.",
  },
  {
    q: "Can I export or share a wellness summary?",
    a: "Where the current product version supports it, you may create a user-controlled summary that distinguishes self-reported information from optional device context. You decide whether to keep or share it. It is not a clinical assessment, a guarantee that a clinician will review it, or continuous clinician monitoring.",
  },
  {
    q: "Does the app respect reduced-motion preferences?",
    a: "The web experience is designed to honor the operating system's reduced-motion preference and may also offer an in-app motion choice. Accessibility behavior must be tested for the current product version; report any motion, keyboard, screen-reader, contrast, or cognitive-load problem to support.",
  },
  {
    q: "Will Myelina recommend medication or treatment changes?",
    a: "No. Myelina does not recommend medication changes, dosage adjustments, disease-modifying therapy changes, or other treatment decisions. Those decisions belong with you and qualified healthcare professionals.",
  },
  {
    q: "I am newly diagnosed. Can Myelina explain what my symptoms mean?",
    a: "No. Myelina does not evaluate a diagnosis, determine what a symptom means, or assess disease course. An adult who meets the current pilot criteria may request access for the limited wellness experience, while continuing to rely on a qualified care team for medical guidance.",
  },
  {
    q: "How is Myelina different from a diary?",
    a: "A diary stores entries. Myelina is intended to organize selected entries and make retrospective patterns easier to review. Any surfaced tendency remains an observation based on the available data, not a forecast, cause, diagnosis, or medical conclusion.",
  },
  {
    q: "Can I stop or change my participation?",
    a: "Participation should remain voluntary. The written pilot and privacy terms should explain how to pause, withdraw, change optional choices, export available information, request deletion, and contact support. A representative should never create a penalty or false deadline for saying no.",
  },
];

/**
 * Combined FAQPage entities, deduped by question text, for JSON-LD.
 * Long FAQ list is authoritative; short FAQ questions merge only when unique.
 */
export const combinedFaqs: FaqItem[] = (() => {
  const seen = new Set(longFaqs.map((faq) => faq.q.toLowerCase()));
  const extras = shortFaqs.filter((faq) => !seen.has(faq.q.toLowerCase()));
  return [...longFaqs, ...extras];
})();
